import { useState, useRef, useEffect } from "react";
import { Avatar, Box, Stack, Typography, Divider, Menu, MenuItem, ListItemIcon, Tooltip, Slide, CircularProgress } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonIcon from "@mui/icons-material/Person";
import LanguageIcon from "@mui/icons-material/Language";
import Logout from "../Logout/Logout";
import DvrIcon from "@mui/icons-material/Dvr";
import TimezoneSelector from "./components/TimezoneSelector";
import { getBrowserTimezoneOffset } from "../../utils/timezoneOffsets";
import ConfirmationModal from "../../components/confirmation-dialog";
import { USER_ROLES } from "../../utils/UserRoles";

const baseApiUrl = `${window.location.origin}/wp-json/tmt-portal/v1`;

const HeaderProfile = () => {
	const { id, roles, active_role, name, last_name, avatar, timezone } = tmtProfile.user_data;

	const [activeRole, setActiveRole] = useState(() => {
		const backendRole = tmtProfile.user_data.active_role;

		return roles.includes(backendRole) ? backendRole : roles[0];
	});
	const hasMultipleRoles = roles && roles.length > 1;

	localStorage.setItem("loggedin_data", JSON.stringify(tmtProfile.user_data));
	localStorage.setItem("va_credits", JSON.stringify(tmtProfile.user_data.va_credits));

	timezone ? localStorage.setItem("userTimezone", timezone) : null;
	id ? localStorage.setItem("id", id) : null;

	const [anchorEl, setAnchorEl] = useState(null);
	const [showTimezoneSelector, setShowTimezoneSelector] = useState(false);
	const [userTimezoneOffset, setUserTimezoneOffset] = useState(getBrowserTimezoneOffset());
	const [switchingRole, setSwitchingRole] = useState(null); // holds the role being switched

	// Modal state
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [pendingTimezoneChange, setPendingTimezoneChange] = useState(null);
	const [isUpdatingTimezone, setIsUpdatingTimezone] = useState(false);

	const triggerRef = useRef(null);

	const updateTimezone = async (timezoneToSave) => {
		setIsUpdatingTimezone(true);
		try {
			const response = await fetch(`${baseApiUrl}/header-profile/update-timezone`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-WP-Nonce": tmtProfile?.nonce,
				},
				body: JSON.stringify({ timezone: timezoneToSave }),
			});

			const data = await response.json();
			if (response.ok) {
				setUserTimezoneOffset(data.timezone);
				localStorage.setItem("userTimezone", data.timezone);
				return { success: true, data };
			} else {
				return {
					success: false,
					error: data.message || "Failed to update timezone",
				};
			}
		} catch (error) {
			console.error("Error updating timezone:", error);
			return { success: false, error: error.message };
		} finally {
			setIsUpdatingTimezone(false);
		}
	};

	useEffect(() => {
		const savedTimezoneOffset = timezone;
		if (savedTimezoneOffset) {
			setUserTimezoneOffset(savedTimezoneOffset);
		} else {
			updateTimezone(userTimezoneOffset);
		}
	}, []);

	useEffect(() => {
		if (active_role) {
			setActiveRole(active_role);
		}
	}, [active_role]);

	const open = Boolean(anchorEl);
	const handleMenuToggle = (event) => {
		if (open) {
			setAnchorEl(null);
		} else {
			setAnchorEl(event.currentTarget);
		}
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		setShowTimezoneSelector(false);
	};

	const handleProfile = () => {
		window.location.href = "/profiles-page/";
		handleMenuClose();
	};

	const handleRoleSwitch = async (role) => {
		setSwitchingRole(role);
		setActiveRole(role);
		localStorage.setItem("active_role", role);
		try {
			const response = await fetch(`${baseApiUrl}/header-profile/switch-role`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-WP-Nonce": tmtProfile?.nonce,
				},
				body: JSON.stringify({ role }),
			});
			const data = await response.json();
			if (response.ok && data.portal_url) {
				window.location.href = data.portal_url;
			} else {
				window.location.reload();
			}
		} catch (e) {
			window.location.reload();
		}
	};

	const handlePortal = () => {
		const currentRole = activeRole || localStorage.getItem("active_role") || roles[0];

		switch (true) {
			case USER_ROLES.admin.includes(currentRole):
				window.location.href = `/admin/employee-management/`;
				break;

			case currentRole === "client":
				window.location.href = `/client/tasks/`;
				break;

			case USER_ROLES.employee.includes(currentRole):
				window.location.href = `/employee/corrective-actions/`;
				break;

			case currentRole === "student":
				window.location.href = `/student/calendar/`;
				break;

			case currentRole === "parent":
				window.location.href = `/parent/calendar/`;
				break;

			case currentRole === "event_volunteer":
				window.location.href = `/attendance/`;
				break;

			default:
				window.location.href = "/";
		}

		handleMenuClose();
	};

	const handleTimezone = () => {
		setShowTimezoneSelector(true);
	};

	const handleTimezoneChange = (newTimezoneOffset) => {
		setPendingTimezoneChange(newTimezoneOffset);
		setConfirmModalOpen(true);
		setShowTimezoneSelector(false);
	};

	const handleTimezoneConfirm = async () => {
		if (pendingTimezoneChange) {
			const result = await updateTimezone(pendingTimezoneChange);

			if (result.success) {
				setConfirmModalOpen(false);
				setPendingTimezoneChange(null);
				handleMenuClose();
				window.location.reload();
			} else {
				console.error("Failed to update timezone:", result.error);
			}
		}
	};

	const handleTimezoneCancel = () => {
		setConfirmModalOpen(false);
		setPendingTimezoneChange(null);
	};

	const handleTimezoneClose = () => {
		setShowTimezoneSelector(false);
	};

	const getTimezoneDisplayText = (offset) => {
		if (!offset) return "Unknown";
		const sign = offset >= 0 ? "+" : "";
		return `UTC ${sign}${offset}`;
	};

	return (
		<>
			<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: "2px", pt: "5px" }}>
				<Divider orientation="vertical" variant="middle" flexItem sx={{ borderColor: "#A9A9A9", borderWidth: 1.2 }} />

				{/* Profile Trigger */}
				<Box
					ref={triggerRef}
					onClick={handleMenuToggle}
					sx={{
						display: "flex",
						alignItems: "center",
						cursor: "pointer",
						ml: "12px !important",
						px: 1,
						py: 0.5,
						borderRadius: 1,
						"&:hover": {
							backgroundColor: "#f5f5f5",
						},
					}}
				>
					{/* Avatar */}
					<Box
						sx={{
							border: `2px solid #1C46B4`,
							borderRadius: "50%",
							padding: "1px",
							display: "inline-block",
							boxSizing: "border-box",
							mr: 1,
						}}
					>
						<Avatar
							src={avatar.attachment_url || `https://api.dicebear.com/7.x/initials/png?seed=${encodeURIComponent(name)}`}
							alt={name}
							sx={{ height: 34, width: 34, backgroundColor: "#fff" }}
						/>
					</Box>
					{/* User Info Header */}
					<Box sx={{ display: "flex", alignItems: "center", mr: 1.2 }}>
						<Box>
							<Typography variant="body2" sx={{ fontWeight: 600, color: "#333" }}>
								{name}
							</Typography>
							<Tooltip title={roles.map((r) => r.charAt(0).toUpperCase() + r.slice(1).toLowerCase()).join(", ")}>
								<Typography variant="caption" sx={{ color: "#666", cursor: "pointer" }}>
									{roles.length > 1
										? `${roles[0].charAt(0).toUpperCase() + roles[0].slice(1).toLowerCase()} (+${roles.length - 1})`
										: roles[0].charAt(0).toUpperCase() + roles[0].slice(1).toLowerCase()}
								</Typography>
							</Tooltip>
						</Box>
					</Box>

					{open ? (
						<ExpandLessIcon sx={{ stroke: "#1C46B4", strokeWidth: 0.5, fontSize: "26px" }} />
					) : (
						<ExpandMoreIcon sx={{ stroke: "#1C46B4", strokeWidth: 0.5, fontSize: "26px" }} />
					)}
				</Box>

				{/* Dropdown Menu */}
				<Menu
					anchorEl={anchorEl}
					open={open}
					onClose={handleMenuClose}
					disableAutoFocusItem
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
					disableScrollLock
					slotProps={{
						paper: {
							sx: {
								mt: 1.2,
								boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.1)",
								borderRadius: 3,
								width: 230,
								backgroundColor: "#F4F8FF",
								overflow: "visible",
								position: "relative",
							},
						},
					}}
				>
					{/* Main Menu content */}
					<Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e0e0e0" }}>
						<Typography variant="body1" sx={{ fontWeight: 600, color: "#1C46B4" }}>
							{name} {last_name}
						</Typography>
						<Typography variant="caption" sx={{ color: "#666" }}>
							{roles.map((r) => r.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())).join(", ")}
						</Typography>
					</Box>

					{/* Menu Items */}
					{/* Role Switcher */}
					{hasMultipleRoles && (
						<>
							<Box sx={{ px: 2, py: 1 }}>
								<Typography
									variant="caption"
									sx={{
										color: "#4B6BA5",
										fontWeight: 600,
										display: "block",
										mb: 0.8,
										letterSpacing: "0.3px",
									}}
								>
									Role View
								</Typography>

								{roles.map((role) => {
									const formattedRole = role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

									const isActive = activeRole === role;
									const isSwitching = switchingRole === role;

									return (
										<Box
											key={role}
											onClick={() => !isActive && !isSwitching && handleRoleSwitch(role)}
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												px: 0.5,
												py: 0.6,
												mb: 0.3,
												cursor: isActive || isSwitching ? "default" : "pointer",
												color: isActive ? "#1C46B4" : "#2D4F8C",
												fontWeight: isActive ? 700 : 500,
												transition: "all 0.15s ease",
												"&:hover": {
													color: isActive ? "#1C46B4" : "#152E8C",
												},
											}}
										>
											<Typography
												variant="body2"
												sx={{
													fontWeight: "inherit",
													color: "inherit",
												}}
											>
												{formattedRole}
											</Typography>
											{isSwitching && (
												<>
													<Typography variant="caption" sx={{ ml: 1, color: "#1C46B4", fontWeight: 700 }}>
														Switching...
													</Typography>
												</>
											)}
											{isActive && !isSwitching && (
												<Typography
													variant="caption"
													sx={{
														color: "#1C46B4",
														fontWeight: 700,
													}}
												>
													✓
												</Typography>
											)}
										</Box>
									);
								})}
							</Box>

							<Divider sx={{ my: 0.5 }} />
						</>
					)}
					<MenuItem onClick={handlePortal} sx={{ py: 1.5, px: 2 }}>
						<ListItemIcon sx={{ minWidth: 36 }}>
							<DvrIcon sx={{ fontSize: 20, color: "#1C46B4" }} />
						</ListItemIcon>
						<Typography variant="body2">Portal</Typography>
					</MenuItem>

					<MenuItem onClick={handleProfile} sx={{ py: 1.5, px: 2 }}>
						<ListItemIcon sx={{ minWidth: 36 }}>
							<PersonIcon sx={{ fontSize: 20, color: "#1C46B4" }} />
						</ListItemIcon>
						<Typography variant="body2">Profile</Typography>
					</MenuItem>

					<MenuItem onClick={handleTimezone} sx={{ py: 1.5, px: 2 }}>
						<ListItemIcon sx={{ minWidth: 36 }}>
							<LanguageIcon sx={{ fontSize: 20, color: "#1C46B4" }} />
						</ListItemIcon>
						<Typography variant="body2">Timezone (UTC {userTimezoneOffset})</Typography>
					</MenuItem>

					<Divider sx={{ my: 0.5 }} />

					<MenuItem sx={{ py: 1.5, px: 2 }}>
						<Logout />
					</MenuItem>

					{/* Timezone Selector Overlay */}
					<TimezoneSelector
						open={showTimezoneSelector}
						onClose={handleTimezoneClose}
						currentTimezoneOffset={userTimezoneOffset}
						onTimezoneChange={handleTimezoneChange}
					/>
				</Menu>
			</Stack>

			{/* Confirmation Modal */}
			<ConfirmationModal
				open={confirmModalOpen}
				onClose={handleTimezoneCancel}
				onConfirm={handleTimezoneConfirm}
				title="Confirm Timezone Change"
				message="Are you sure you want to change your timezone? This will affect how dates and times are displayed throughout the application."
				confirmText="Update Timezone"
				cancelText="Cancel"
				variant="confirm"
				isLoading={isUpdatingTimezone}
				highlightedContent={{
					"Current Timezone Offset": getTimezoneDisplayText(userTimezoneOffset),
					"New Timezone Offset": getTimezoneDisplayText(pendingTimezoneChange),
				}}
			/>
		</>
	);
};

export default HeaderProfile;
